import dayjs from "dayjs";
import { groupBy } from "lodash";
import Utils from "../Utils";
import i18n from "../i18n";
import router from "../router";
import services from "./../services";
import NestMoney from "../NestMoney";

const strict = true;

const getDefaultState = () => {
  return {
    appointmentInfo: {},
    createdAppointmentId: "",
    currentStep: Utils.BOOKING_STEPS.SERVICE,
    bond: "",
    type: "SLOT",
    typeMap: {
      SLOT: Utils.BOOKING_STEPS.CALENDAR,
      TIMEPERIOD: Utils.BOOKING_STEPS.PORTAL_DATE,
      BYPHONE: Utils.BOOKING_STEPS.PORTAL_DATE
    },
    //  --- B O O K I N G  W I Z A R D  S T E P S ---
    // id: 0 Beneficiary
    // id: 1 Office
    // id: 2 Service
    // id: 3 Bookable months
    // id: 4 Diagnostic Question
    // id: 5 Doctor
    // id: 6 Calendar (BYSLOT)
    // id: 7 Portal Date (BYPORTAL)
    // id: 8 Summary
    // id: 9 Feedback
    steps: {
      // preloaded step
      [Utils.BOOKING_STEPS.BENEFICIARY]: {
        id: Utils.BOOKING_STEPS.BENEFICIARY,
        name: "beneficiary",
        completed: true,
        visited: true,
        active: false,
        inMenu: true,
        title: "bookingWizard.beneficiaryTitle",
        menuTitle: "bookingWizard.menu.beneficiary",
        selectedInfo: "",
        icon: "person",
        isValid: true,
        nextStep: () => {},
        prevStep: () => {},
        info: {
          beneficiary: {}
        },
        component: ""
      },
      // preloaded step
      [Utils.BOOKING_STEPS.OFFICE]: {
        id: Utils.BOOKING_STEPS.OFFICE,
        name: "struttura",
        completed: true,
        visited: true,
        active: false,
        inMenu: true,
        title: "bookingWizard.officeTitle",
        menuTitle: "bookingWizard.menu.office",
        icon: "fas fa-clinic-medical",
        selectedInfo: "",
        searchResults: [],
        searchText: "",
        isValid: true,
        nextStep: () => {},
        prevStep: () => {},
        info: {
          office: {}
        },
        component: "select-structure"
      },
      // first valid step
      [Utils.BOOKING_STEPS.SERVICE]: {
        id: Utils.BOOKING_STEPS.SERVICE,
        name: "prestazione",
        completed: false,
        visited: true,
        active: true,
        inMenu: true,
        title: "bookingWizard.service.title",
        menuTitle: "bookingWizard.menu.service",
        selectedInfo: "",
        icon: "widgets",
        searchResults: [],
        searchText: "",
        searchHasBeenPerformed: false,
        instructions: "bookingWizard.service.instructions",
        instructionsSani: "bookingWizard.service.instructionsSani",
        isValid: state => {
          let service = state.steps[Utils.BOOKING_STEPS.SERVICE].info.service;
          return service?.serviceId;
        },
        nextStep: () => Utils.BOOKING_STEPS.COVERAGE,
        prevStep: () => {
          return {}; // this is the first valid step, so no previous step is defined
        },
        info: {
          service: {},
          services: [],
          residualCeiling: 0,
          exemption: 0,
          messages: [],
          bookableMonths: [],
          nextTwoBookableMonths: [],
          currentYearResidualCeiling: {},
          nextYearResidualCeiling: {},
          ancillaries: [],
          selectedAncillaries: []
        },
        component: "service"
      },
      [Utils.BOOKING_STEPS.COVERAGE]: {
        id: Utils.BOOKING_STEPS.COVERAGE,
        name: "mesiPrenotabili",
        completed: false,
        visited: false,
        active: false,
        inMenu: false,
        title: "bookingWizard.bookableMonthsTitle",
        menuTitle: "bookingWizard.menu.bookableMonthsTitle",
        selectedInfo: "",
        icon: "contact_support",
        instructions: "bookingWizard.bookableMonths.instructions",
        instructionsSani: "bookingWizard.bookableMonths.instructionsSani",
        isValid: () => {
          return true;
        },
        nextStep: () => {
          return Utils.BOOKING_STEPS.DIAGNOSIS;
        },
        prevStep: () => {
          return Utils.BOOKING_STEPS.SERVICE;
        },
        info: {},
        component: "service-bookable-months"
      },
      [Utils.BOOKING_STEPS.DIAGNOSIS]: {
        id: Utils.BOOKING_STEPS.DIAGNOSIS,
        name: "quesitoDiagnostico",
        completed: false,
        visited: false,
        active: false,
        inMenu: true,
        title: "bookingWizard.pathologyTitle",
        menuTitle: "bookingWizard.menu.diagnosticQuestion",
        selectedInfo: "",
        icon: "contact_support",
        instructions: "bookingWizard.pathology.instructions",
        instructionsSani: "bookingWizard.pathology.instructionsSani",
        isValid: state => {
          return state.steps[Utils.BOOKING_STEPS.DIAGNOSIS].info.pathology;
        },
        nextStep: state => {
          let service = state.steps[Utils.BOOKING_STEPS.SERVICE].info.service;
          return service.macroArea === "VSP"
            ? Utils.BOOKING_STEPS.DOCTOR
            : state.typeMap[state.type];
        },
        prevStep: () => {
          return Utils.BOOKING_STEPS.COVERAGE;
        },
        info: {
          pathology: "",
          constraint: {
            obese: false,
            claustrophobia: false
          }
        },
        component: "service-diagnostic-question"
      },
      [Utils.BOOKING_STEPS.DOCTOR]: {
        id: Utils.BOOKING_STEPS.DOCTOR,
        name: "medico",
        completed: false,
        visited: false,
        active: false,
        inMenu: state => {
          let service = state.steps[Utils.BOOKING_STEPS.SERVICE].info.service;
          return service && service.macroArea === "VSP";
        },
        title: "bookingWizard.doctorsTitle",
        menuTitle: "bookingWizard.menu.doctor",
        icon: "fas fa-user-md",
        selectedInfo: "",
        instructions: "bookingWizard.doctors.instructions",
        instructionsSani: null,
        searchResults: [],
        searchText: "",
        isValid: () => {
          return true;
        },
        nextStep: state => {
          return state.type === "SLOT"
            ? Utils.BOOKING_STEPS.CALENDAR
            : Utils.BOOKING_STEPS.PORTAL_DATE;
        },
        prevStep: () => {
          return Utils.BOOKING_STEPS.DIAGNOSIS;
        },
        info: {
          doctor: {},
          doctors: []
        },
        component: "select-doctor"
      },
      [Utils.BOOKING_STEPS.CALENDAR]: {
        id: Utils.BOOKING_STEPS.CALENDAR,
        name: "calendario",
        completed: false,
        visited: false,
        active: false,
        inMenu: state => {
          let bookingType =
            state.steps[Utils.BOOKING_STEPS.SERVICE].info.service.bookingType;
          return bookingType === "SLOT";
        },
        title: "bookingWizard.dateTimeTitle",
        menuTitle: "bookingWizard.menu.dateTime",
        icon: "fas fa-calendar-day",
        selectedInfo: "",
        isValid: state => {
          let dateTime =
            state.steps[Utils.BOOKING_STEPS.CALENDAR].info.slot.dateTime;
          let customDate = state.steps[Utils.BOOKING_STEPS.CALENDAR].info.date;
          if (dateTime) {
            return true;
          }
          return state.bond === "DATE" && customDate;
        },
        nextStep: () => {
          return Utils.BOOKING_STEPS.SUMMARY;
        },
        prevStep: state => {
          let service = state.steps[Utils.BOOKING_STEPS.SERVICE].info.service;
          return service && service.macroArea === "VSP"
            ? Utils.BOOKING_STEPS.DOCTOR
            : Utils.BOOKING_STEPS.DIAGNOSIS;
        },
        info: {
          date: "",
          from: "",
          to: "",
          slots: [],
          slot: {},
          slotDuration: 0,
          slotOffset: 0
        },
        component: "calendar-view"
      },
      [Utils.BOOKING_STEPS.PORTAL_DATE]: {
        id: Utils.BOOKING_STEPS.PORTAL_DATE,
        name: "portale",
        completed: false,
        visited: false,
        active: false,
        inMenu: state => {
          let bookingType =
            state.steps[Utils.BOOKING_STEPS.SERVICE].info.service.bookingType;
          return bookingType === "TIMEPERIOD";
        },
        title: "bookingWizard.dateTimeTitle",
        menuTitle: "bookingWizard.menu.dateTime",
        icon: "fas fa-calendar-day",
        selectedInfo: "",
        isValid: true,
        nextStep: () => {
          return Utils.BOOKING_STEPS.SUMMARY;
        },
        prevStep: state => {
          let service = state.steps[Utils.BOOKING_STEPS.SERVICE].info.service;
          return service && service.macroArea === "VSP"
            ? Utils.BOOKING_STEPS.DOCTOR
            : Utils.BOOKING_STEPS.DIAGNOSIS;
        },
        info: {
          portalDate: "",
          portalTime: ""
        },
        component: "portal-date-view"
      },
      [Utils.BOOKING_STEPS.SUMMARY]: {
        id: Utils.BOOKING_STEPS.SUMMARY,
        name: "riassunto",
        inMenu: true,
        completed: false,
        visited: false,
        active: false,
        title: "bookingWizard.summary",
        menuTitle: "bookingWizard.menu.summary",
        icon: "date_range",
        selectedInfo: "",
        isValid: () => {
          return true;
        },
        nextStep: () => {
          return Utils.BOOKING_STEPS.FEEDBACK;
        },
        prevStep: state => {
          let bookingType = state.type;
          return state.typeMap[bookingType];
        },
        info: {
          operationalService: {}
        },
        component: "final-booking-recap"
      },
      [Utils.BOOKING_STEPS.FEEDBACK]: {
        id: Utils.BOOKING_STEPS.FEEDBACK,
        name: "feedback",
        completed: false,
        visited: false,
        active: false,
        inMenu: false,
        title: "bookingWizard.feedbackTitle",
        menuTitle: "bookingWizard.menu.feedback",
        icon: "notifications",
        selectedInfo: "",
        isValid: () => {
          return true;
        },
        nextStep: () => {},
        prevStep: () => {},
        info: {},
        component: "booking-feedback"
      }
    },
    /**
     * Other configurations to preload the calendar properly
     */
    initialMonthOffset: 0,
    firstBookableDate: dayjs().format("YYYY-MM-DD")
  };
};

const state = getDefaultState();

const getters = {
  getCurrentStep: state => {
    return state.steps[state.currentStep];
  },
  getCurrentStepIndex: state => state.currentStep,
  getSteps: state => state.steps,
  getStepByName: state => name => {
    for (let key in state.steps) {
      if (Object.hasOwn(state.steps, key)) {
        let step = state.steps[key];
        if (step.name == name) return step;
      }
    }
    return null;
  },
  getMenuSteps: state => {
    let filtered = {};
    for (let key in state.steps) {
      if (Object.hasOwn(state.steps, key)) {
        let step = state.steps[key];
        let include = false;

        if (typeof step.inMenu === "function") include = step.inMenu(state);
        else include = step.inMenu;

        if (include)
          filtered[step.id] = {
            ...step
          };
      }
    }
    return filtered;
  },
  getSearchInput: state => state.steps[state.currentStep].info.searchInput,
  getSearchResults: state => step => {
    return state.steps[step || state.currentStep].searchResults;
  },
  getSearchHasBeenPerformed: state => step =>
    state.steps[step || state.currentStep].searchHasBeenPerformed || false,
  getSearchText: state => step =>
    state.steps[step || state.currentStep].searchText || "",
  getBeneficiary: state =>
    state.steps[Utils.BOOKING_STEPS.BENEFICIARY].info.beneficiary,
  getOffice: state => state.steps[Utils.BOOKING_STEPS.OFFICE].info.office,
  getService: state => state.steps[Utils.BOOKING_STEPS.SERVICE].info.service,
  getServices: state => state.steps[Utils.BOOKING_STEPS.SERVICE].info.services,
  getExemption: state =>
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.exemption,
  getBookableMonths: state =>
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.bookableMonths,
  getNextTwoBookableMonths: state =>
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.nextTwoBookableMonths,
  getCurrentYearResidualCeiling: state =>
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.currentYearResidualCeiling,
  getNextYearResidualCeiling: state =>
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.nextYearResidualCeiling,
  getAncillaries: state =>
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.ancillaries,
  getSelectedAncillaries: state =>
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.selectedAncillaries,

  getConstraints: state => {
    let constraint = {};
    constraint["obese"] =
      state.steps[Utils.BOOKING_STEPS.DIAGNOSIS].info.constraint.obese || false;
    constraint["claustrophobia"] =
      state.steps[Utils.BOOKING_STEPS.DIAGNOSIS].info.constraint
        .claustrophobia || false;
    return constraint;
  },
  getPathology: state =>
    state.steps[Utils.BOOKING_STEPS.DIAGNOSIS].info.pathology,
  hasPathology: state => state.steps[Utils.BOOKING_STEPS.SERVICE].hasPathology,
  getDoctor: state => state.steps[Utils.BOOKING_STEPS.DOCTOR].info.doctor,
  getDoctors: state => state.steps[Utils.BOOKING_STEPS.DOCTOR].info.doctors,
  getCustomDate: state => {
    let info = state.steps[Utils.BOOKING_STEPS.SUMMARY].info;
    if (info.date && info.from) {
      let date = dayjs(`${info.date}T${info.from}:00.000Z`);
      return {
        dateTime: date.toDate().toISOString(),
        date: info.date,
        from: info.from,
        to: info.to
      };
    } else if (info.date && !info.from) {
      return {
        date: info.date
      };
    } else {
      return undefined;
    }
  },
  getSlot: state => state.steps[Utils.BOOKING_STEPS.CALENDAR].info.slot,
  getSlots: state => state.steps[Utils.BOOKING_STEPS.CALENDAR].info.slots,
  getSlotDuration: state =>
    state.steps[Utils.BOOKING_STEPS.CALENDAR].info.slotDuration,
  getSlotOffset: state =>
    state.steps[Utils.BOOKING_STEPS.CALENDAR].info.slotOffset,
  getPortalDate: state =>
    state.steps[Utils.BOOKING_STEPS.PORTAL_DATE].info.portalDate,
  getPortalTime: state =>
    state.steps[Utils.BOOKING_STEPS.PORTAL_DATE].info.portalTime,
  getResidualCeiling: state =>
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.residualCeiling,
  getOperationalService: state =>
    state.steps[Utils.BOOKING_STEPS.SUMMARY].info.operationalService,
  getServiceMessages: state =>
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.messages,
  toEdit: state => state.toEdit,
  isValid: state => state.steps[state.currentStep].isValid(state),
  getBond: state => state.bond,
  getDocuments: state =>
    state.steps[Utils.BOOKING_STEPS.COVERAGE].info.documents,
  getAppointmentType: state => state.type,
  /**
   * @returns the appointment info after its creation
   */
  getAppointmentInfo: state => state.appointmentInfo,
  getAppointmentId: state => state.createdAppointmentId,
  getInitialMonthOffset: state => state.initialMonthOffset,
  getFirstBookableDate: state => state.firstBookableDate
};

const mutations = {
  nextStep(state) {
    // 1. set properties of the completed step
    state.steps[state.currentStep].active = false;
    state.steps[state.currentStep].completed = true;
    // 2. get the next step
    state.currentStep = state.steps[state.currentStep].nextStep(state);
    // 3. set properties of the next step
    state.steps[state.currentStep].active = true;
    state.steps[state.currentStep].visited = true;
    state.steps[state.currentStep].completed = false;
    // 4. go to step
    router.push({
      name: "bookingWizard",
      query: {
        q: state.steps[state.currentStep].name
      }
    });
  },
  previousStep(state) {
    // 1. reset properties of the leaving step
    resetSteps(state.steps, state.currentStep, state.currentStep + 1);
    // 2. get the next step
    state.currentStep = state.steps[state.currentStep].prevStep(state);
    // 3. set properties of the next step
    state.steps[state.currentStep].active = true;
    state.steps[state.currentStep].visited = true;
    state.steps[state.currentStep].completed = false;
    // 4. go to step
    router.go(-1);
  },
  editStep(state, id) {
    // 1. reset properties of the leaving step
    resetSteps(state.steps, state.currentStep, state.currentStep + 1);
    // 2. get the next step based on the requested id of the step to edit
    state.currentStep = state.steps[id].id;
    // 3. set properties of the next step
    state.steps[state.currentStep].active = true;
    state.steps[state.currentStep].visited = true;
    state.steps[state.currentStep].completed = false;
    // 4. reset all the steps starting from the new current step plus 1
    resetSteps(state.steps, state.currentStep + 1, 10);
    // 5. go to step
    router.push({
      name: "bookingWizard",
      query: { q: state.steps[state.currentStep].name }
    });
  },
  searchHasBeenPerformed(state, input) {
    state.steps[state.currentStep].searchHasBeenPerformed = input;
  },
  searchText(state, input) {
    state.steps[state.currentStep].searchText = input;
  },
  searchResults(state, { results, step }) {
    state.steps[step || state.currentStep].searchResults = results;
  },
  beneficiary(state, item) {
    state.steps[Utils.BOOKING_STEPS.BENEFICIARY].info.beneficiary = item;
    state.steps[
      Utils.BOOKING_STEPS.BENEFICIARY
    ].selectedInfo = `${item.nome} ${item.cognome}`;
  },
  office(state, office) {
    state.steps[Utils.BOOKING_STEPS.OFFICE].info.office = office;
    if (office?.id && office?.legalName) {
      let t = i18n ? i18n.t("bookingWizard.office") : "Struttura Sanitaria";
      state.steps[Utils.BOOKING_STEPS.OFFICE].selectedInfo = t
        .concat(": ")
        .concat(office.legalName);
      state.type = office.bookingType;
    }
  },
  service(state, service) {
    let selectedServiceUpdated = false;
    let oldService = state.steps[Utils.BOOKING_STEPS.SERVICE].info.service;
    if (oldService && oldService.serviceId != service.serviceId) {
      selectedServiceUpdated = true;
    }
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.service = service;
    state.type = service.bookingType;
    if (selectedServiceUpdated) {
      resetSteps(
        state.steps,
        Utils.BOOKING_STEPS.COVERAGE,
        Utils.BOOKING_STEPS.FEEDBACK
      );
    }
  },
  setServiceSelectedInfo(state) {
    let service = state.steps[Utils.BOOKING_STEPS.SERVICE].info.service;
    let ancillariesInfo =
      state.steps[Utils.BOOKING_STEPS.SERVICE].info.selectedAncillaries || [];
    let serviceName = service
      ? service.label?.defaultValue || service.nameByLang || ""
      : "";
    let sn = `Prestazione richiesta: ${serviceName}`;
    let ancillaryNames = ancillariesInfo
      .map(a => `${a.count}x ${a.description}`)
      .join("</br>");
    let sa = ancillaryNames
      ? `Prestazioni ancillari:</br>${ancillaryNames}`
      : null;
    let info = [sn, sa].filter(Boolean);
    let formattedInfo = info.join("</br>");
    state.steps[Utils.BOOKING_STEPS.SERVICE].selectedInfo = formattedInfo;
  },
  services(state, services) {
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.services = services;
  },
  messages(state, messages) {
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.messages = messages;
  },
  bookableMonths(state, months) {
    const maxMonth = dayjs().add(1, "month");
    const availableBm = months.filter(m =>
      dayjs(`${m.year}-${m.month}-01`).isSameOrBefore(maxMonth, "month")
    );
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.bookableMonths = months;
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.nextTwoBookableMonths =
      availableBm;
  },
  currentYearResidualCeiling(state, ceiling) {
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.currentYearResidualCeiling =
      ceiling;
  },
  nextYearResidualCeiling(state, ceiling) {
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.nextYearResidualCeiling =
      ceiling;
  },
  setAncillaries(state, ancillaries) {
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.ancillaries = ancillaries;
  },
  setSelectedAncillaries(state, ancillaries) {
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.selectedAncillaries =
      ancillaries;
  },
  removeAncillaryFromSelectdList(state, index) {
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.selectedAncillaries.splice(
      index,
      1
    );
  },
  updateAncillaryCount(state, { index, count }) {
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.selectedAncillaries[
      index
    ].count = count;
  },
  pathologyDescription(state, description) {
    state.steps[Utils.BOOKING_STEPS.DIAGNOSIS].info.pathology = description;
    state.steps[Utils.BOOKING_STEPS.DIAGNOSIS].selectedInfo = description;
  },
  constraints(state, constraint) {
    state.steps[Utils.BOOKING_STEPS.DIAGNOSIS].info.constraint = constraint;
  },
  hasPathology(state, hasPathology) {
    state.steps[Utils.BOOKING_STEPS.SERVICE].hasPathology = hasPathology;
  },
  doctor(state, doctor) {
    let d = doctor;
    if (Object.keys(doctor).length === 0) {
      d.name = "nessuna";
      d.surname = "preferenza";
    }
    state.steps[Utils.BOOKING_STEPS.DOCTOR].info.doctor = d;
    let t = i18n ? i18n.t("bookingWizard.doctor") : "Medico";
    state.steps[Utils.BOOKING_STEPS.DOCTOR].selectedInfo = t
      .concat(": ")
      .concat(`${d.name} ${d.surname}`);
  },
  doctors(state, results) {
    state.steps[Utils.BOOKING_STEPS.DOCTOR].info.doctors = results;
  },
  slots(state, slots) {
    state.steps[Utils.BOOKING_STEPS.CALENDAR].info.slots = slots;
  },
  slot(state, slot) {
    state.steps[Utils.BOOKING_STEPS.CALENDAR].info.slot = slot;
    state.steps[Utils.BOOKING_STEPS.CALENDAR].selectedInfo = `${dayjs(
      slot.dateTime
    ).format("dddd DD MMMM")} ${i18n.t("bookingWizard.toTime")} ${dayjs(
      slot.dateTime
    ).format("HH:mm")}`;
  },
  slotDuration(state, duration) {
    state.steps[Utils.BOOKING_STEPS.CALENDAR].info.slotDuration = duration;
  },
  slotOffset(state, slotOffset) {
    state.steps[Utils.BOOKING_STEPS.CALENDAR].info.slotOffset = slotOffset;
  },
  customDate(state, { date, from, to }) {
    state.steps[Utils.BOOKING_STEPS.SUMMARY].info.date = date;
    state.steps[Utils.BOOKING_STEPS.SUMMARY].info.from = from;
    state.steps[Utils.BOOKING_STEPS.SUMMARY].info.to = to;
  },
  SET_PORTAL_DATE(state, date) {
    let dateStep = state.steps[Utils.BOOKING_STEPS.PORTAL_DATE];
    dateStep.info.portalDate = date;
    let fullDate = `${date}T${dateStep.info.portalTime}`;
    dateStep.selectedInfo = `${dayjs(fullDate).format("dddd DD MMMM")} ${i18n.t(
      "bookingWizard.toTime"
    )} ${dayjs(fullDate).format("HH:mm")}`;
  },
  SET_PORTAL_TIME(state, time) {
    let dateStep = state.steps[Utils.BOOKING_STEPS.PORTAL_DATE];
    dateStep.info.portalTime = time;
    let fullDate = `${dateStep.info.portalDate}T${time}`;
    dateStep.selectedInfo = `${dayjs(fullDate).format("dddd DD MMMM")} ${i18n.t(
      "bookingWizard.toTime"
    )} ${dayjs(fullDate).format("HH:mm")}`;
  },
  type(state, t) {
    state.type = t;
  },
  residualCeiling(state, ceiling) {
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.residualCeiling = ceiling;
  },
  operationalService(state, service) {
    state.steps[Utils.BOOKING_STEPS.SUMMARY].info.operationalService = service;
  },
  bond(state, bond) {
    state.bond = bond;
  },
  addDocument(state, document) {
    state.steps[Utils.BOOKING_STEPS.COVERAGE].info.documents.push(document);
  },
  deleteDocument(state, document) {
    let idx = state.steps[
      Utils.BOOKING_STEPS.COVERAGE
    ].info.documents.findIndex(doc => doc.name === document.name);
    if (idx > -1)
      state.steps[Utils.BOOKING_STEPS.COVERAGE].info.documents.splice(idx, 1);
  },
  exemption(state, exemption) {
    state.steps[Utils.BOOKING_STEPS.SERVICE].info.exemption = exemption;
  },
  appointmentInfo(state, appointment) {
    state.appointmentInfo = appointment;
    state.createdAppointmentId = appointment.appointmentId;
  },
  async RESET(state) {
    //  acquire initial state
    const s = getDefaultState();
    Object.keys(s).forEach(key => {
      state[key] = s[key];
    });
    resetSteps(state.steps);
    state.steps[Utils.BOOKING_STEPS.SERVICE].completed = false;
    state.steps[Utils.BOOKING_STEPS.SERVICE].visited = true;
    state.steps[Utils.BOOKING_STEPS.SERVICE].active = true;
  },
  setCalendarConf(state) {
    let firstBookableMonth =
      state.steps[Utils.BOOKING_STEPS.SERVICE].info.nextTwoBookableMonths[0];
    let firstBookableDate = dayjs(
      `${firstBookableMonth.year}-0${firstBookableMonth.month}-01`
    );
    let monthOffset = firstBookableDate.diff(
      dayjs().date(1).format("YYYY-MM-DD"),
      "month"
    );
    state.initialMonthOffset = monthOffset;
    if (monthOffset === 0) {
      state.firstBookableDate = dayjs().format("YYYY-MM-DD");
      state.steps[Utils.BOOKING_STEPS.PORTAL_DATE].info.portalDate =
        dayjs().format("YYYY-MM-DD");
    } else {
      state.firstBookableDate = dayjs(firstBookableDate).format("YYYY-MM-DD");
      state.steps[Utils.BOOKING_STEPS.PORTAL_DATE].info.portalDate =
        dayjs(firstBookableDate).format("YYYY-MM-DD");
    }
  }
};

const actions = {
  async initialize({ rootGetters, commit }) {
    try {
      commit("office", rootGetters["hfManagement/selectedHfOffice"]);
    } catch (error) {
      console.error(error);
    }
  },
  async getServices({ getters, commit }, search) {
    try {
      let searchQuery = search.searchQuery
        ? search.searchQuery.replace(/([^-]* - )/g, "")
        : "";
      if (searchQuery.length >= 3) {
        let serviceList = await services.bookingsService.getServiceNames({
          search: searchQuery || "",
          officeId: getters["getOffice"].id
        });
        serviceList = sortByKey(serviceList, "nameByLang");
        commit("searchResults", {
          results: serviceList,
          step: Utils.BOOKING_STEPS.SERVICE
        });
        if (!search.autocomplete) {
          commit("searchHasBeenPerformed", true);
          commit("services", serviceList);
        }
      } else {
        // Sets empty for searchResults of the step
        commit("searchResults", {
          results: [],
          step: Utils.BOOKING_STEPS.SERVICE
        });
      }
    } catch (error) {
      console.error(error);
    }
  },
  async getDoctors({ commit, getters }, search) {
    try {
      let seperatorIdx = search.searchQuery
        ? search.searchQuery.indexOf("-")
        : -1;
      let searchedOfficeName =
        seperatorIdx > -1
          ? search.searchQuery.substring(
              seperatorIdx + 2,
              search.searchQuery.length
            )
          : "";
      search.searchQuery =
        seperatorIdx > -1
          ? search.searchQuery.slice(0, seperatorIdx - 1)
          : search.searchQuery;
      let doctors = await services.bookingsService.getDoctors({
        toSearch: search.searchQuery,
        officeServiceId: getters["getOffice"].officeId,
        province: getters["getOffice"].address.province.code,
        serviceId: getters["getService"].serviceId,
        ...getters["getConstraints"]
      });
      commit("searchResults", {
        results: doctors
      });
      if (!search.autocomplete) {
        // Filter doctors to see if they are available for the service
        // This need to be done in the api
        let filteredDoctors = {};
        let tempDoctors = [];
        if (searchedOfficeName) {
          doctors = doctors.filter(
            doc => doc.officeName === searchedOfficeName
          );
        }
        filteredDoctors = groupBy(doctors, "doctorId");
        let ids = Object.keys(filteredDoctors);
        ids.forEach(id => {
          let doc = filteredDoctors[id].find(d => d.deliverRequestedService);
          if (!doc) doc = filteredDoctors[id][0];
          tempDoctors.push(doc);
        });
        for (let doctor of tempDoctors) {
          let office = await services.bookingsService.getOfficeService({
            officeserviceId: doctor.officeServiceId
          });
          if (doctor.offices) doctor.offices.push(office);
          else doctor["office"] = office;
        }
        commit("doctors", tempDoctors);
      }
    } catch (error) {
      let errorDialogDetails = {
        ...error
      };
      if (error.type == Utils.errorTypes.STATUS) {
        errorDialogDetails.dataName = "booking";
      }

      commit("error/SET_ERROR", errorDialogDetails, {
        root: true
      });
    }
  },
  async getDoctorsByOfficeService({ commit, getters }) {
    try {
      let doctors = await services.bookingsService.getDoctorsByOfficeService({
        id: getters["getOffice"].id,
        serviceId: getters["getService"].serviceId
      });
      commit("doctors", doctors);
    } catch (error) {
      console.error(error);
    }
  },
  async setService({ commit, getters }, result) {
    try {
      let ancillaries = getters["getSelectedAncillaries"] || [];
      ancillaries = ancillaries.map(a => ({
        ancillaryId: a.id,
        num: a.count
      }));
      let service = await services.bookingsService.getServiceWithId({
        serviceId: result.serviceId,
        officeId: getters["getOffice"].id,
        ancillaryIds: ancillaries
      });
      commit(
        "exemption",
        service.exemption ? service.exemption.slice(4, -1) : 0
      );
      service["nameByLang"] = result.nameByLang;
      commit("service", service);
    } catch (error) {
      let errorDialogDetails = {
        ...error
      };
      if (error.type == Utils.errorTypes.STATUS) {
        errorDialogDetails.dataName = "booking";
      }
      commit("error/SET_ERROR", errorDialogDetails, {
        root: true
      });
    }
  },
  async goOnFromServiceStep({ commit }) {
    commit("loading/OPEN_LOADING_DIALOG", null, {
      root: true
    });
    commit("nextStep");
    commit("loading/CLOSE_LOADING_DIALOG", null, {
      root: true
    });
  },
  async calculateBookableMonthsWithBasedCriteria({
    getters,
    commit,
    rootGetters
  }) {
    try {
      commit("messages", []);
      let messages = [];
      let bookableMonths = [];
      let service = getters["getService"];
      let serviceId = service.serviceId;
      let servicePrice = Number.parseFloat(
        NestMoney.formatCurrencyForOP(service.price)
      );
      let ancillariesPrice = Number.parseFloat(
        NestMoney.formatCurrencyForOP(service.ancillariesPrice)
      );
      let totalPrice = servicePrice + ancillariesPrice;
      // call to get the booking info for the given member, service and office
      let bookingInfo =
        await services.bookingsService.callGetServiceBookingInfo({
          serviceId: serviceId,
          officeId: getters["getOffice"].id,
          memberFiscalCode: getters["getBeneficiary"].codiceFiscale
        });
      if (Object.keys(bookingInfo).length) {
        let thisYear = dayjs().year();
        /**
         * First step
         *  - check if the number of maximum booked for the current year is ok
         *  - check if the residual ceiling for the current year is ok
         * Only if both are ok:
         *  - build the bookable months for the current year
         *  - check the residual ceiling for the current year againts the service min and service max prices
         */
        let currentMaxAllowedHealthCare =
          bookingInfo[`${thisYear}`].residualCeiling.maxAllowedHealthCare;
        let currentResidualCeiling =
          bookingInfo[`${thisYear}`].residualCeiling.residualCeiling;
        if (currentMaxAllowedHealthCare <= 0 || currentResidualCeiling <= 0) {
          if (currentMaxAllowedHealthCare <= 0) {
            messages.push(
              "bookingWizard.bookableMonths.exceededThisYearHealthCare"
            );
          }
          if (currentResidualCeiling <= 0) {
            messages.push(
              "bookingWizard.bookableMonths.exceededThisYearCeilings"
            );
          }
        } else {
          // build the bookable/reminder months for the current year
          bookableMonths.push(
            ...bookingInfo[`${thisYear}`].coverage.map(month => ({
              year: thisYear,
              isBookable:
                month.bookableByRule &&
                !month.notCovered &&
                !month.maxCeilingReached &&
                !month.maxHealthCareReached,
              ...month
            }))
          );
          // check the residual ceiling for the current year againts the service price
          let currentResidual = bookingInfo[`${thisYear}`].residualCeiling;
          commit("currentYearResidualCeiling", currentResidual);
          let currentResidualCeiling = currentResidual.residualCeiling;
          if (currentResidualCeiling && currentResidualCeiling < totalPrice) {
            messages.push(
              "bookingWizard.bookableMonths.currentYearResidualZeroMin"
            );
          }
        }
        /**
         * Second step
         *  - check if is possible to book for the next year
         * Only if is ok:
         *  - check if the number of maximum booked for the next year is ok
         *  - check if the residual ceiling for the next year is ok
         * Only if both are ok:
         *  - build the bookable months for the next year
         *  - check the residual ceiling for the next year againts the service price
         */
        let nextYear = bookingInfo[`${thisYear + 1}`];
        if (nextYear) {
          let nextYearBookingIsPossible = nextYear.coverage;
          nextYearBookingIsPossible = nextYearBookingIsPossible.filter(
            coverage =>
              !coverage.bookableByRule &&
              !coverage.notCovered &&
              !coverage.maxCeilingReached &&
              !coverage.maxHealthCareReached
          );
          if (nextYearBookingIsPossible.length) {
            messages.push("bookingWizard.bookableMonths.nextYearCanBook");
            let nextMaxAllowedHealthCare =
              nextYear.residualCeiling.maxAllowedHealthCare;
            let nextResidualCeiling = nextYear.residualCeiling.residualCeiling;
            if (nextMaxAllowedHealthCare <= 0 || nextResidualCeiling <= 0) {
              if (nextMaxAllowedHealthCare <= 0) {
                messages.push(
                  "bookingWizard.bookableMonths.exceededNextYearHealthCare"
                );
              }
              if (nextResidualCeiling <= 0) {
                messages.push(
                  "bookingWizard.bookableMonths.exceededNextYearCeilings"
                );
              }
            } else {
              // build the bookable/reminder months for the next year
              bookableMonths.push(
                ...nextYear.coverage.map(month => ({
                  year: thisYear + 1,
                  isBookable:
                    month.bookableByRule &&
                    !month.notCovered &&
                    !month.maxCeilingReached &&
                    !month.maxHealthCareReached,
                  ...month
                }))
              );
              // check the residual ceiling for the next year againts the service min and service max prices
              let nextResidual = nextYear.residualCeiling;
              commit("nextYearResidualCeiling", nextResidual);
              let nextResidualCeiling = nextResidual.residualCeiling;
              if (nextResidualCeiling && nextResidualCeiling < totalPrice) {
                messages.push(
                  "bookingWizard.bookableMonths.nextYearResidualZeroMin"
                );
              }
            }
          } else {
            // build the reminder months for the next year
            bookableMonths.push(
              ...nextYear.coverage.map(month => ({
                year: thisYear + 1,
                isBookable:
                  month.bookableByRule &&
                  !month.notCovered &&
                  !month.maxCeilingReached &&
                  !month.maxHealthCareReached,
                ...month
              }))
            );
          }
        }
      }
      let bookableMonthsGroupedByCodicePacchetto =
        groupBookableMonthsByCodicePacchetto(bookableMonths);
      let isSanimpresaOffice = rootGetters["hfManagement/isSanimpresaOffice"];
      if (isSanimpresaOffice) {
        // filter bookable months with only months that are bookable and have codicePacchetto "SANI"
        let saniBookableMonths = bookableMonthsGroupedByCodicePacchetto.filter(
          month => month.isBookable && month.codicePacchetto.includes("SANI")
        );
        if (saniBookableMonths && saniBookableMonths.length === 0) {
          messages.push("bookingWizard.bookableMonths.noSanimpresaCoverage");
        }
        commit("bookableMonths", saniBookableMonths);
      } else {
        // filter bookable months with only months that are bookable
        commit(
          "bookableMonths",
          bookableMonthsGroupedByCodicePacchetto.filter(
            month => month.isBookable
          )
        );
      }
      commit("messages", messages);
      return;
    } catch (error) {
      console.error(error);
    }
  },
  setOffice({ commit }, result) {
    commit("office", result);
    commit("nextStep");
  },
  async setDoctor({ commit }, doctor) {
    commit("doctor", doctor);
    commit("nextStep");
  },
  async getSlots({ commit, getters }, monthOffset) {
    commit("slotOffset", monthOffset);
    try {
      commit("loading/OPEN_LOADING_DIALOG", null, {
        root: true
      });
      let res;
      let nextAvailableMonth;
      do {
        res = await services.bookingsService.getSlotsForMonths({
          id: getters["getOffice"].id,
          monthOffset: monthOffset,
          officeserviceId: getters["getService"].officeServiceId,
          obese: getters["getConstraints"].obese,
          doctorId: getters["getDoctor"].doctorId || null,
          claustrophobia: getters["getConstraints"].claustrophobia
        });
        if (res?.slotList && res?.slotList?.length === 0) {
          let bookableMonths = getters["getNextTwoBookableMonths"];
          let thisMonth = dayjs().set("date", 1);
          let requestedMonth = thisMonth.add(monthOffset, "month");
          nextAvailableMonth = bookableMonths.find(m =>
            requestedMonth.isBefore(dayjs(`${m.year}-${m.month}-01`))
          );
          monthOffset = nextAvailableMonth
            ? Math.abs(
                dayjs(
                  `${nextAvailableMonth.year}-${nextAvailableMonth.month}-01`
                ).month() - thisMonth.month()
              )
            : monthOffset;
        } else {
          commit("slotOffset", monthOffset);
          commit("slots", res.slotList);
          commit("slotDuration", res.duration.value);
        }
      } while (res?.slotList?.length === 0 && nextAvailableMonth !== undefined);
    } catch (error) {
      if (error?.response?.headers["x-onair-exception-type"]) {
        if (
          error.response.headers["x-onair-exception-type"] !=
          "nest.common.InvalidSlotException"
        ) {
          let errorDialogDetails = {
            ...error
          };
          if (error.type == Utils.errorTypes.STATUS) {
            errorDialogDetails.dataName = "booking";
          }
          commit("error/SET_ERROR", errorDialogDetails, {
            root: true
          });
        }
      }
    } finally {
      commit("loading/CLOSE_LOADING_DIALOG", null, {
        root: true
      });
    }
  },
  async getOperationalService({ commit, getters }) {
    let operationalService;
    try {
      let officeServiceId = getters["getService"].officeServiceId;
      operationalService = await services.bookingsService.getOperationalService(
        {
          officeServiceId
        }
      );
      commit("operationalService", operationalService);
    } catch (error) {
      let errorDetails = {
        type: Utils.errorTypes.STATUS,
        reason: {
          status: 500
        }
      };
      commit("error/SET_ERROR", errorDetails, {
        root: true
      });
    } finally {
      commit("loading/CLOSE_LOADING_DIALOG", null, {
        root: true
      });
      // return operationalService;
    }
  },
  async getMoreSlots({ commit, getters }) {
    commit("loading/OPEN_LOADING_DIALOG", null, {
      root: true
    });
    commit("slotOffset", (getters["getSlotOffset"] || 0) + 1);
    let slots = await services.bookingsService.getSlotsForMonths({
      id: getters["getOffice"].id,
      monthOffset: getters["getSlotOffset"] || 0,
      officeserviceId:
        getters["getBond"] === "DOCTOR"
          ? getters["getDoctor"].officeServiceId
          : getters["getOffice"].officeServiceId,
      obese: getters["getConstraints"].obese,
      doctorId: getters["getDoctor"].doctorId || null,
      claustrophobia: getters["getConstraints"].claustrophobia
    });
    commit("slots", [...getters["getSlots"], ...slots.slotList]);
    commit("slotDuration", slots.duration.value);
  },
  /**
   * Constructs the appointment request taking info from the steps of the wizard
   * @param {*}
   */
  async constructAppointment({ commit, getters }) {
    commit("loading/OPEN_LOADING_DIALOG", null, {
      root: true
    });
    let appointment = {};
    let province = getters["getOffice"].address.province;
    appointment["province"] = province;
    appointment["serviceId"] = getters["getService"].serviceId;
    appointment["hfOfficeId"] = getters["getOffice"].id;
    appointment["appointmentType"] = getters["getAppointmentType"];
    appointment["doctorId"] = getters["getDoctor"].doctorId || null;
    appointment["pathology"] = getters["getPathology"];
    appointment["constraint"] = getters["getConstraints"];
    appointment["appointmentFlowSearchType"] = "NONE";
    if (getters["getAppointmentType"] === "SLOT") {
      let slot = getters["getSlot"];
      if (slot["dateTime"]) {
        appointment["appointmentDate"] = dayjs(slot["dateTime"]).format();
      }
    }
    if (getters["getAppointmentType"] === "TIMEPERIOD") {
      let portalDate = getters["getPortalDate"];
      let portalTime = getters["getPortalTime"];
      let requestedDates = [];
      let obj = {
        day: portalDate,
        fromTime: `${portalTime}:00`,
        toTime: null
      };
      requestedDates.push(obj);
      appointment["requestDateWithTimes"] = requestedDates;
    }
    //ancillaries
    let ancillaries = getters["getSelectedAncillaries"] || [];
    ancillaries = ancillaries.map(a => ({
      ancillaryId: a.id,
      num: a.count
    }));
    appointment["ancillaryIds"] = ancillaries;
    try {
      let params = {
        officeId: getters["getOffice"].id,
        memberFiscalCode: getters["getBeneficiary"].codiceFiscale
      };
      let a = await services.bookingsService.sendAppointment(
        params,
        appointment
      );
      commit("appointmentInfo", a);
      commit("nextStep");
    } catch (error) {
      let errorDialogDetails = {
        reason: {
          status: 412
        }
      };
      if (error.type == Utils.errorTypes.STATUS) {
        errorDialogDetails.dataName = "booking";
      }
      commit("error/SET_ERROR", errorDialogDetails, {
        root: true
      });
    } finally {
      commit("loading/CLOSE_LOADING_DIALOG", null, {
        root: true
      });
    }
  },
  async retrieveAncillaries({ commit, rootGetters }, serviceId) {
    try {
      let result = await services.appointmentsService.getAncillariesList({
        officeId: rootGetters["hfManagement/officeId"],
        serviceId: serviceId
      });
      let ancillaries = result?.ancillaryList ?? [];
      if (ancillaries.length > 0) {
        ancillaries = sortByKey(ancillaries, "description", "asc");
      }
      commit("setAncillaries", ancillaries);
      return ancillaries;
    } catch (error) {
      let errorDialogDetails = {
        ...error
      };
      if (error.type == Utils.errorTypes.STATUS) {
        errorDialogDetails.dataName = "appointments";
      }
      commit("error/SET_ERROR", errorDialogDetails, {
        root: true
      });
    }
  }
};

const sortByKey = (array, key, order) => {
  if (order === "asc" || !order) {
    return array.sort((a, b) => {
      let aVal = typeof a[key] === "string" ? a[key].toLowerCase() : a[key];
      let bVal = typeof b[key] === "string" ? b[key].toLowerCase() : b[key];
      if (aVal < bVal) return -1;
      else if (aVal > bVal) return 1;
      else return 0;
    });
  } else {
    return array.sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      if (aVal < bVal) return 1;
      else if (aVal > bVal) return -1;
      else return 0;
    });
  }
};

const resetSteps = (steps, start, end) => {
  start = start || 0;
  end = end || steps.length;
  for (let i = start; i < end; i++) {
    let step = steps[i];
    step.active = false;
    step.completed = false;
    step.visited = false;
    step.selectedInfo = "";
    step.searchResults = [];
    step.searchText = "";
    step.searchHasBeenPerformed = false;
    Object.keys(step.info).forEach(key => {
      if (step.info[key]) {
        if (typeof step.info[key] === "object") {
          if (step.info[key].length === undefined) {
            step.info[key] = {};
          } else {
            step.info[key] = [];
          }
        } else if (typeof step.info[key] === "string") {
          step.info[key] = "";
        } else if (typeof step.info[key] === "number") {
          step.info[key] = 0;
        }
      }
    });
  }
  return steps;
};

function groupBookableMonthsByCodicePacchetto(bookableMonths) {
  if (bookableMonths?.length === 0) {
    return [];
  }
  let firstMonth = bookableMonths.slice(0, 1)[0];
  let startYear = firstMonth.year;
  let startMonth =
    firstMonth.month.length === 1 ? `0${firstMonth.month}` : firstMonth.month;
  let lastMonth = bookableMonths.slice(-1)[0];
  let endYear = lastMonth.year;
  let endMonth =
    lastMonth.month.length === 1 ? `0${lastMonth.month}` : lastMonth.month;
  let from = dayjs(`${startYear}-${startMonth}-01`);
  let to = dayjs(`${endYear}-${endMonth}-01`);
  let months = [];
  let monthDate = from;
  while (monthDate.isSame(to) || monthDate.isBefore(to)) {
    let searchedMonths = bookableMonths.filter(
      coverage =>
        monthDate.year() === coverage.year &&
        monthDate.month() === coverage.month - 1
    );
    if (searchedMonths.length > 0) {
      let result = searchedMonths.reduce((acc, curr) => {
        acc.bookableByRule = curr.bookableByRule;
        acc.codicePacchetto = curr.codicePacchetto;
        acc.healthCareResidual = curr.healthCareResidual;
        acc.isBookable = curr.isBookable;
        acc.maxCeilingReached = curr.maxCeilingReached;
        acc.maxHealthCareReached = curr.maxHealthCareReached;
        acc.month = curr.month;
        acc.notCovered = curr.notCovered;
        acc.year = curr.year;
        return acc;
      }, {});
      months.push(result);
    } else {
      months.push({
        bookableByRule: false,
        codicePacchetto: [],
        healthCareResidual: 0,
        isBookable: false,
        maxCeilingReached: true,
        maxHealthCareReached: true,
        month: monthDate.month() + 1,
        notCovered: true,
        year: monthDate.year()
      });
    }
    monthDate = monthDate.add(1, "month");
  }
  // fondoest/issues#1098
  months.forEach(m => {
    if (m.codicePacchetto.includes("U1")) {
      m.codicePacchetto = m.codicePacchetto.filter(cp => cp !== "U1");
    }
    if (m.codicePacchetto.includes("MH")) {
      m.codicePacchetto = m.codicePacchetto.filter(cp => cp !== "MH");
    }
  });
  return months;
}

export default {
  namespaced: true,
  strict,
  state,
  getters,
  mutations,
  actions
};
